<template>
  <div class="d-flex align-center flex-wrap dialog-actions">
    <ontos-header-action v-if="fullPageRoute || onFullPage" icon="mdi-open-in-new"
                         :label="$tt('expand')"
                         :disabled="disabled" @click="leave('full-page')"/>
    <!-- Voltar no lugar do fechar quando há para onde voltar DENTRO da janela: o X encerra a
         janela, a seta anda um passo nela. O ícone passa a dizer qual das duas coisas acontece.
         Sempre só ícone, em qualquer largura. -->
    <v-btn size="small" variant="text" icon :title="$tt(back ? 'back' : 'close')"
           :disabled="disabled" @click="leave(back ? 'back' : 'close')">
      <v-icon>{{ back ? 'mdi-arrow-left' : 'mdi-close' }}</v-icon>
    </v-btn>

    <ontos-unsaved-changes-dialog :leaving="leaving"
                                  @confirmed="confirmLeave" @cancelled="leaving = null"/>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {RouteLocationRaw} from 'vue-router'
import UnsavedChangesDialog, {Leave} from '@/components/shared/UnsavedChangesDialog.vue'
import {markExpanding} from '@/utils/motion'

/**
 * As ações de canto de um modal: o atalho para a página inteira e o fechar.
 *
 * Existe para o par aparecer igual em toda janela — antes o bloco estava copiado em
 * cada diálogo, junto de um openFullPage idêntico. A navegação mora aqui: quem usa
 * só diz para onde.
 *
 * Com rascunho pendente as saídas passam pelo aviso, que é o `UnsavedChangesDialog` — a mesma
 * peça que a rota de editar usa no Voltar dela.
 */
@Component({
  emits: ['closed'],
  name: 'ontos-dialog-actions',
  components: {'ontos-unsaved-changes-dialog': UnsavedChangesDialog}
})
class DialogActions extends Vue {
  /** Destino do "abrir em página inteira". Sem ele e sem o `onFullPage`, o atalho não aparece. */
  @Prop({type: Object, default: null}) readonly fullPageRoute!: RouteLocationRaw | null
  /**
   * Abrir em página inteira LEVANDO o que está na tela, em vez de só empurrar a rota.
   *
   * Havendo-a, o atalho aparece, ela é chamada no lugar da navegação — e **o aviso de rascunho
   * é pulado nessa saída**: quem a passou disse que carrega o que foi digitado, e a edição
   * continua de onde parou do outro lado. Avisar de uma perda que não acontece seria mentira.
   *
   * As outras duas saídas continuam avisando, porque nelas o rascunho se perde de verdade.
   */
  @Prop({type: Function, default: null}) readonly onFullPage!: (() => void) | null
  /** Trava os botões enquanto o host grava */
  @Prop({type: Boolean, default: false}) readonly disabled!: boolean
  /**
   * Há o que perder: as saídas passam a avisar antes. Sem Cancelar no rodapé, o X é o caminho
   * normal de desistir — e desistir sem querer não pode ser mudo.
   */
  @Prop({type: Boolean, default: false}) readonly dirty!: boolean
  /**
   * A janela tem para onde VOLTAR: o fechar vira uma seta, e sair daqui devolve o conteúdo
   * anterior em vez de encerrar. É o caso da edição dentro da janela de uma instância.
   */
  @Prop({type: Boolean, default: false}) readonly back!: boolean

  /** Saída esperando confirmação, ou null quando não há aviso na tela */
  leaving: Leave | null = null

  leave(how: Leave) {
    //A página inteira com carga não perde nada: sai direto, mesmo com rascunho
    if (this.dirty && !(how === 'full-page' && this.onFullPage)) this.leaving = how
    else this.go(how)
  }

  confirmLeave() {
    const how = this.leaving
    this.leaving = null
    if (how) this.go(how)
  }

  //O evento é um só para as três saídas: quem hospeda é que sabe se `closed` quer dizer fechar
  //a janela ou voltar um passo dentro dela — e foi ele quem pediu a seta.
  private go(how: Leave) {
    if (how === 'full-page') {
      //A janela sai crescendo até a página, e não encolhendo como no fechar — ver o `exitOf`
      markExpanding()
      //Com carga, quem navega é ela: só ela sabe o que vai junto
      if (this.onFullPage) this.onFullPage()
      //Empurrar a rota em que já se está rejeita com NavigationDuplicated
      else if (this.fullPageRoute) this.$router.push(this.fullPageRoute).catch(() => undefined)
    }
    this.$emit('closed')
  }
}

export default toNative(DialogActions)
</script>

<style scoped>
/* Mesmo respiro entre botões que o grupo de ações do cabeçalho usa */
.dialog-actions {
  gap: 8px;
}
</style>
